import React, { useState, useMemo } from "react";
import { Box, Container, Typography, Grid, Button, TextField, InputAdornment, Chip, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { Rocket, Wrench, BrainCircuit, MessageCircle, BarChart3, Settings, Webhook, HelpCircle, ChevronDown, ChevronRight, Mail, BookOpen, Users, Bot, Globe, Code } from "lucide-react";
import BRAND from "../theme/talktokam";
import { StepList, TipBox } from "../components/HelpArticle";

const popularArticles = [
  { title: "Creating your first bot", icon: Rocket, category: "getting-started" },
  { title: "Embedding the chat widget", icon: Globe, category: "getting-started" },
  { title: "Setting up AI Agent", icon: BrainCircuit, category: "ai-agents" },
  { title: "Connecting live chat", icon: MessageCircle, category: "live-chat" },
  { title: "Managing your plan", icon: Settings, category: "account" },
];

const categories = [
  {
    id: "getting-started", icon: Rocket, title: "Getting Started", color: "#38bdf8",
    desc: "Set up your account, create your first bot, and embed it on your website.",
    articles: [
      {
        title: "Quick Start Guide",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Get your chatbot live in under 5 minutes. Follow these simple steps to create, configure, and launch your first bot.
            </Typography>
            <StepList steps={[
              "Create your account at talktokam.com — no credit card required",
              "Go to Dashboard and click 'Create New Bot'. Give it a name and select your industry",
              "Customize the bot's appearance in Settings: choose colors, logo, and widget position",
              "Build your conversation flow using the Flow Builder or enable AI for smart replies",
              "Copy the embed code from Settings → Installation and paste it into your website",
              "Test your bot by visiting your website or using the direct share link",
            ]} />
            <TipBox type="tip" text="You can preview your bot anytime using the 'Preview' button in the bot dashboard before making it live." />
          </>
        ),
      },
      {
        title: "Creating Your First Bot",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              A bot is your automated assistant that talks to website visitors. Here's how to set one up from scratch.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 2 }}>
              Navigate to your Dashboard and click the "Create Bot" button. You'll be asked to enter a bot name — choose something descriptive like "Support Bot" or "Sales Assistant". You can also select your industry to get pre-configured settings.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 2 }}>
              After creation, you'll land on the bot dashboard where you can configure everything: the welcome message, theme colors, AI settings, and the conversation flow. Each bot has its own dedicated dashboard with analytics, conversations, and settings tabs.
            </Typography>
            <TipBox type="warn" text="Bots are private by default. You must explicitly enable them in Settings → Status to make them live on your website." />
          </>
        ),
      },
      {
        title: "Embedding the Chat Widget",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Add the chatbot to any website using a simple JavaScript snippet. Works with all website builders and platforms.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 2 }}>
              Go to Settings → Installation in your bot dashboard. You'll find two options:
            </Typography>
            <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#38bdf8", mb: 0.5 }}>JavaScript Embed</Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>Copy the embed script and paste it just before the closing {"</body>"} tag on your website. Works with WordPress, Shopify, Wix, Squarespace, and custom HTML sites.</Typography>
            </Box>
            <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#38bdf8", mb: 0.5 }}>Direct Link</Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>Share a direct link to your chatbot. Perfect for email campaigns, WhatsApp, social media bios, and QR codes.</Typography>
            </Box>
            <TipBox type="tip" text="Use the domain whitelist feature to restrict your bot to specific domains. This prevents others from embedding your bot on unauthorized sites." />
          </>
        ),
      },
      {
        title: "Bot Settings Overview",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Every bot has a comprehensive settings panel where you control its behavior, appearance, and capabilities.
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mb: 2 }}>
              {[
                { s: "General", d: "Bot name, status, default language, timezone" },
                { s: "Appearance", d: "Colors, logo, avatar, widget position, button text" },
                { s: "AI Settings", d: "Enable AI, select model, API key configuration" },
                { s: "Behavior", d: "Auto-trigger delay, exit intent, GDPR consent mode" },
                { s: "Security", d: "Domain whitelist, rate limiting, spam protection" },
                { s: "Installation", d: "Embed code, direct link, WordPress plugin" },
              ].map((item, i) => (
                <Box key={i} sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#e2e8f0", mb: 0.3 }}>{item.s}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>{item.d}</Typography>
                </Box>
              ))}
            </Box>
          </>
        ),
      },
      {
        title: "Dashboard Tour",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              The dashboard is your command center. Here's what you'll find in each section.
            </Typography>
            <StepList steps={[
              "Dashboard: Overview of total conversations, leads captured, and performance metrics",
              "Chats: View all conversations with visitors, see message history and visitor details",
              "Leads: Captured emails, phone numbers, and visitor information organized in a table",
              "Analytics: Charts and reports showing conversation volume, lead capture rate, and bot performance",
              "Settings: All bot configuration options including appearance, AI, security, and team access",
              "Flow Builder: Design conversation paths with 19+ question types and conditional logic",
            ]} />
          </>
        ),
      },
    ],
  },
  {
    id: "bot-building", icon: Wrench, title: "Bot Building", color: "#a855f7",
    desc: "Master the flow editor with 19 question types, conditions, and variables.",
    articles: [
      {
        title: "Flow Builder Overview",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              The Flow Builder is a visual drag-and-drop editor where you design your chatbot's conversation paths. Each conversation is made up of questions that the bot asks and the visitor answers.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 2 }}>
              Questions are connected in sequence — when a visitor answers, the bot moves to the next question. You can create branches, skip questions based on answers, and capture data at every step.
            </Typography>
            <TipBox type="tip" text="Start simple with 5-7 questions and expand your flow as you learn. A shorter bot is better than a confusing one." />
          </>
        ),
      },
      {
        title: "Question Types Reference",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Choose from 19 question types to build engaging conversations. Each type serves a different purpose.
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 1, mb: 2 }}>
              {[
                "Text Input", "Email", "Phone", "Website URL", "Number",
                "Buttons", "Dropdown", "Checkboxes", "File Upload", "Rating",
                "Date Picker", "Appointment", "Order Items", "Carousel", "Form",
                "Ask Contacts", "OTP Verify", "Real-time Search", "API Call",
              ].map((t, i) => (
                <Box key={i} sx={{ px: 1.5, py: 0.8, borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <Typography sx={{ fontSize: "0.75rem", color: "#cbd5e1" }}>{t}</Typography>
                </Box>
              ))}
            </Box>
            <TipBox type="warn" text="Some question types like Appointment Booking, Order Items, and File Upload are available on Starter+ plans." />
          </>
        ),
      },
      {
        title: "Using Conditions & Jumps",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Make your bot smarter by routing visitors based on their answers. Use conditions to show different follow-up questions to different people.
            </Typography>
            <StepList steps={[
              "Add a question with multiple choice options (e.g., 'Are you a new or existing customer?')",
              "Click the 'Add Condition' button below the question",
              "Set the condition: if answer equals 'New Customer', jump to question 5",
              "Set another condition: if answer equals 'Existing Customer', jump to question 8",
              "Test your flow by clicking 'Preview' and selecting different answers",
            ]} />
            <TipBox type="tip" text="Use conditions to create personalized experiences. For example, ask different questions based on the visitor's selected interest or industry." />
          </>
        ),
      },
      {
        title: "Variables & Data Referencing",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Use variables to personalize conversations. Reference previous answers anywhere in your flow using curly braces.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 1 }}>
              Available variables: {"{name}"}, {"{email}"}, {"{phone}"}, {"{Q1}"}, {"{Q2}"} (where Q1 is the first question ID), {"{greeting}"} (time-based greeting), {"{time_emoji}"}
            </Typography>
            <TipBox type="tip" text="Use {name} in your welcome message to greet visitors personally: 'Hi {name}, welcome to our site!'" />
          </>
        ),
      },
    ],
  },
  {
    id: "ai-agents", icon: BrainCircuit, title: "AI Agents", color: "#10b981",
    desc: "Configure intelligent AI agents with lead capture, human handoff, and website context.",
    articles: [
      {
        title: "Creating an AI Agent",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              AI Agents are smart assistants powered by Groq's Llama 3.1 or OpenAI GPT models. They can answer visitor questions, capture leads, and escalate to humans when needed.
            </Typography>
            <StepList steps={[
              "Go to AI Agents in the sidebar and click 'Create Agent'",
              "Choose a template: Sales, Support, Lead Qualifier, Appointment Booker, or FAQ",
              "Configure the agent's name, tone (professional, friendly, casual), and goal",
              "Add your website URL so the AI can learn about your business (optional)",
              "Upload training Q&A pairs for custom knowledge (optional)",
              "Enable the agent and set it live on your website",
            ]} />
            <TipBox type="tip" text="AI Agents work alongside your flow builder. You can use the flow for structured questions and the AI for open-ended conversations." />
          </>
        ),
      },
      {
        title: "Customizing Greetings",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              First impressions matter. Customize how your AI Agent greets website visitors to match your brand voice.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 2 }}>
              You can set a custom greeting message, suggested questions (up to 4 that appear as quick reply buttons), and the agent's avatar. The greeting appears when a visitor first opens the chat widget.
            </Typography>
            <TipBox type="tip" text="Use suggested questions to guide visitors toward common topics. For example: 'How much does it cost?', 'What features do you offer?'" />
          </>
        ),
      },
      {
        title: "Lead Capture & Human Handoff",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              AI Agents automatically detect emails and phone numbers in visitor messages and save them as leads. You get instant email notifications for every new lead.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 2 }}>
              When a visitor asks to speak to a human, the AI detects keywords like "live agent" or "talk to a person" and flags the conversation for human intervention. You'll receive an email alert and can take over the conversation from the dashboard.
            </Typography>
            <TipBox type="warn" text="For human handoff to work, Live Chat must be enabled in Settings. Assign agents in the Team page to receive handoff requests." />
          </>
        ),
      },
      {
        title: "Agent Best Practices",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Follow these best practices to get the most out of your AI Agents.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
              {[
                { q: "Set clear goals", a: "Choose a specific goal for each agent — lead collection, FAQ answering, or appointment booking. Don't mix goals in one agent." },
                { q: "Provide website context", a: "Add your website URL so the AI can learn about your products, services, and company info. This dramatically improves answer quality." },
                { q: "Train with Q&A pairs", a: "Upload 5-10 common questions and answers to improve accuracy for your specific use case." },
                { q: "Monitor analytics", a: "Check the Agent Chats dashboard regularly to review conversations and identify areas for improvement." },
              ].map((item, i) => (
                <Box key={i} sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#10b981", mb: 0.3 }}>{item.q}</Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>{item.a}</Typography>
                </Box>
              ))}
            </Box>
          </>
        ),
      },
    ],
  },
  {
    id: "ai-knowledge", icon: Bot, title: "AI Bot & Knowledge Base", color: "#6366f1",
    desc: "Upload PDFs, crawl websites, and build a knowledge base for smarter AI responses.",
    articles: [
      {
        title: "AI Bot Settings",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Enable AI in your bot settings to let the bot answer questions intelligently. When AI is enabled, the bot can handle questions that aren't in your flow.
            </Typography>
            <StepList steps={[
              "Go to Bot Settings → AI Configuration",
              "Toggle 'Enable AI Responses' to ON",
              "Choose your AI provider: OpenAI (GPT-3.5/4) or Groq (Llama 3.1)",
              "Enter your API key (for OpenAI) or leave blank to use the built-in Groq key",
              "Select the model: GPT-3.5 Turbo, GPT-4o Mini, GPT-4o, or Llama 3.1 8B",
              "Save settings and test with the Preview button",
            ]} />
          </>
        ),
      },
      {
        title: "PDF Knowledge Upload",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Upload PDF documents to create a knowledge base that the AI can search for accurate answers. Supports RAG (Retrieval-Augmented Generation) for precise document-based responses.
            </Typography>
            <StepList steps={[
              "Go to Knowledge Base in your bot dashboard",
              "Click 'Upload PDF' and select your document (max 10MB)",
              "Supported formats: PDF, DOC, DOCX, TXT",
              "The system processes the document and creates embeddings using Hugging Face models",
              "Once processed, the AI automatically searches the knowledge base when answering questions",
            ]} />
            <TipBox type="tip" text="For best results, upload documents with clear headings and structured content. PDFs with tables and lists work especially well." />
          </>
        ),
      },
      {
        title: "Website Crawl (RAG)",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Automatically crawl your website pages to build a knowledge base. The AI extracts content from your pages and uses it to answer visitor questions.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 2 }}>
              Enter your website URL in the AI Agent settings. The system uses Puppeteer to crawl the page, extracts text content (removing scripts and styles), and caches it for 1 hour. The AI then references this content when answering questions.
            </Typography>
            <TipBox type="warn" text="Website crawling respects robots.txt and blocks private IPs. Make sure your site is publicly accessible for crawling to work." />
          </>
        ),
      },
      {
        title: "AI Message Limits",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Each plan includes a daily limit on AI-powered messages. Here's how they work:
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr 1fr" }, gap: 1, mb: 2, textAlign: "center" }}>
              {[
                { p: "Free", limit: "50/day" },
                { p: "Starter", limit: "500/day" },
                { p: "Growth", limit: "2000/day" },
              ].map((item, i) => (
                <Box key={i} sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#e2e8f0" }}>{item.p}</Typography>
                  <Typography sx={{ fontSize: "1.2rem", fontWeight: 800, color: "#38bdf8", my: 0.5 }}>{item.limit}</Typography>
                  <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>AI messages</Typography>
                </Box>
              ))}
            </Box>
            <TipBox type="tip" text="Unused AI messages don't roll over. Upgrade your plan if you consistently hit the limit." />
          </>
        ),
      },
    ],
  },
  {
    id: "live-chat", icon: MessageCircle, title: "Live Chat", color: "#f59e0b",
    desc: "Set up real-time conversations with visitors. Manage agents, queues, and handoffs.",
    articles: [
      {
        title: "Enabling Live Chat",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Live Chat lets you and your team talk to website visitors in real time. Available on Starter+ plans.
            </Typography>
            <StepList steps={[
              "Go to Settings → Live Chat and toggle 'Enable Live Chat' to ON",
              "Set working hours and maximum chats per agent",
              "Add team members from the Team page — they'll receive chat requests",
              "Configure auto-assignment rules (round-robin or skill-based)",
              "Save and test by visiting your website",
            ]} />
            <TipBox type="tip" text="Enable 'Live Chat Suggestion' in AI settings to let the AI suggest connecting to a live agent when it can't answer a question." />
          </>
        ),
      },
      {
        title: "Agent Queue Management",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              When multiple visitors request live chat, they're placed in a queue. Agents can see the queue and accept chats one at a time.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 2 }}>
              The queue shows visitor name, wait time, and the conversation context (previous bot messages). Agents can accept, decline, or transfer chats. You can set the maximum number of concurrent chats per agent in Settings.
            </Typography>
          </>
        ),
      },
      {
        title: "Canned Responses",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Save time with pre-written replies. Create canned responses for common questions and use slash commands to insert them instantly.
            </Typography>
            <StepList steps={[
              "Go to Settings → Canned Responses",
              "Click 'Add Response' and enter a shortcut like /greeting",
              "Write the response text (supports formatting)",
              "Save — agents can now type /greeting in live chat to insert the response",
              "Organize responses into categories for easy management",
            ]} />
          </>
        ),
      },
      {
        title: "Chat Transfer & Resolve",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Agents can transfer active chats to other team members or resolve conversations when done.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 2 }}>
              To transfer, click the transfer button in the chat header and select the target agent. Resolved chats are moved to the history and the visitor is prompted to rate their experience. You can view all resolved conversations in the Chats page.
            </Typography>
          </>
        ),
      },
    ],
  },
  {
    id: "analytics", icon: BarChart3, title: "Analytics & Reports", color: "#22d3ee",
    desc: "Track performance, measure lead capture, and understand visitor behavior.",
    articles: [
      {
        title: "Dashboard Overview",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              The Analytics Dashboard gives you a comprehensive view of your bot's performance with real-time metrics and charts.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 2 }}>
              Key metrics include: Total Conversations, Messages Sent, Leads Captured, Lead Conversion Rate, Active Users, and Average Response Time. Each metric can be viewed across different time ranges (24h, 7d, 30d, 90d) and filtered by bot.
            </Typography>
            <TipBox type="tip" text="Use the time range filter to compare week-over-week performance. Look for trends in lead capture rate to optimize your flow." />
          </>
        ),
      },
      {
        title: "Understanding Analytics Charts",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              The charts section visualizes your data to help you make informed decisions.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
              {[
                { l: "Conversations Over Time", d: "Line chart showing daily/weekly conversation volume" },
                { l: "Lead Capture Trend", d: "Track how many leads you're capturing each day" },
                { l: "Response Time Distribution", d: "Histogram showing how fast your bot responds" },
                { l: "Popular Questions", d: "Bar chart of most frequently asked questions" },
                { l: "Visitor Geography", d: "Map and list of visitor locations" },
              ].map((item, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1.5, alignItems: "center", p: 1, borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.02)" }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#22d3ee", flexShrink: 0 }} />
                  <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#e2e8f0", minWidth: 180 }}>{item.l}</Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>{item.d}</Typography>
                </Box>
              ))}
            </Box>
          </>
        ),
      },
    ],
  },
  {
    id: "account", icon: Settings, title: "Account & Billing", color: "#f472b6",
    desc: "Manage your profile, team, subscription, and billing settings.",
    articles: [
      {
        title: "Account Settings",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Manage your profile information, password, and security settings from the Account page.
            </Typography>
            <StepList steps={[
              "Navigate to Account from the sidebar or profile dropdown",
              "Update your name, email, and profile picture",
              "Change your password — you'll need to enter your current password",
              "Enable Two-Factor Authentication (2FA) for extra security",
              "View your API tokens and generate new ones for API access",
            ]} />
            <TipBox type="warn" text="Always enable Two-Factor Authentication if you have team members accessing your account. It prevents unauthorized access even if your password is compromised." />
          </>
        ),
      },
      {
        title: "Team Management",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Add team members and assign roles to collaborate on bot management.
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1, mb: 2 }}>
              {[
                { r: "Admin", p: "Full access — billing, settings, team management, all bots" },
                { r: "Analyst", p: "View analytics and reports. No editing permissions" },
                { r: "Supervisor", p: "Manage live chat, view conversations, assign agents" },
                { r: "Agent", p: "Handle live chat conversations only" },
                { r: "User", p: "Basic access to assigned bots" },
              ].map((item, i) => (
                <Box key={i} sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#f472b6", mb: 0.3 }}>{item.r}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>{item.p}</Typography>
                </Box>
              ))}
            </Box>
          </>
        ),
      },
      {
        title: "Plan Limits and Upgrades",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Choose a plan that fits your needs. Each plan has different limits on bots, AI messages, team members, and features.
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr 1fr" }, gap: 1, mb: 2, textAlign: "center" }}>
              {[
                { p: "Free", bots: "1 bot", ai: "50/day", team: "1 user" },
                { p: "Starter", bots: "3 bots", ai: "500/day", team: "5 users" },
                { p: "Growth", bots: "10 bots", ai: "2000/day", team: "Unlimited" },
              ].map((item, i) => (
                <Box key={i} sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#e2e8f0", mb: 1 }}>{item.p}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>🤖 {item.bots}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>⚡ {item.ai}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>👥 {item.team}</Typography>
                </Box>
              ))}
            </Box>
            <TipBox type="tip" text="You can upgrade or downgrade anytime from the Plans page. Changes take effect immediately and are prorated." />
          </>
        ),
      },
    ],
  },
  {
    id: "integrations", icon: Webhook, title: "Integrations & API", color: "#8b5cf6",
    desc: "Connect your bot with external tools via webhooks, API, and third-party services.",
    articles: [
      {
        title: "Webhook Integrations",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              Webhooks send real-time data from your chatbot to any external system. Whenever an event happens (new lead, conversation resolved, etc.), we send an HTTP POST request to your webhook URL.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 2 }}>
              Available events: new_lead, chat_resolved, new_conversation, live_requested, agent_message, rating_received, human_requested. Each event includes relevant data like visitor name, email, phone, and conversation transcript. Failed deliveries are retried up to 3 times with a 5-second delay.
            </Typography>
            <TipBox type="tip" text="Use webhooks to connect with Zapier, HubSpot, Salesforce, Slack, Google Sheets, or any custom endpoint." />
          </>
        ),
      },
      {
        title: "REST API Overview",
        content: (
          <>
            <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
              TalkToKam provides a comprehensive REST API for programmatic access to all features. You can manage bots, conversations, AI agents, analytics, and more.
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7, mb: 2 }}>
              Generate an API token from Settings → API. Use the token in the Authorization header: "Bearer your_token_here". The API is organized into 17 route groups with 120+ endpoints covering everything from bot management to webhook configuration.
            </Typography>
            <Button variant="outlined" onClick={() => window.open("/api-docs", "_self")} sx={{ borderRadius: "8px", textTransform: "none", fontSize: "0.8rem", borderColor: "rgba(139,92,246,0.3)", color: "#8b5cf6", "&:hover": { bgcolor: "rgba(139,92,246,0.1)", borderColor: "#8b5cf6" } }}>
              <Code size={14} style={{ marginRight: 6 }} /> View Full API Documentation
            </Button>
          </>
        ),
      },
    ],
  },
];

const faqLeftItems = [
  { q: "What is a website chatbot?", a: "A website chatbot is an automated conversational tool that talks to your website visitors in real time. It can answer questions, collect leads, schedule appointments, and provide 24/7 support without human intervention." },
  { q: "How does AI website bot work?", a: "AI bots use large language models (like GPT-4 and Llama 3.1) to understand visitor questions and generate human-like responses. They can also reference your knowledge base (PDFs, website content) to provide accurate answers about your business." },
  { q: "Can I customize chatbot flows?", a: "Yes! The Flow Builder lets you design custom conversation paths with 19 question types, conditional logic, variables, and skip patterns. You can create anything from simple FAQ bots to complex multi-step sales funnels." },
  { q: "Can I train the chatbot using my website?", a: "Absolutely. You can upload PDF documents or provide your website URL for automatic crawling. The AI uses RAG (Retrieval-Augmented Generation) to search this knowledge base and provide accurate responses." },
  { q: "Does it support live chat?", a: "Yes, Starter+ plans include live chat functionality. Visitors can seamlessly transition from AI bot to human agent. Features include agent queue management, canned responses, chat transfer, and real-time messaging." },
  { q: "Is coding required?", a: "Not at all. The entire platform is no-code. The visual Flow Builder, drag-and-drop interface, and configuration forms make it easy to set up and manage your chatbot without writing any code." },
];

const faqRightItems = [
  { q: "Can I integrate with CRM tools?", a: "Yes, through webhooks you can connect with HubSpot, Salesforce, Zoho CRM, Slack, Google Sheets, and any custom endpoint. We also have a full REST API for custom integrations." },
  { q: "How quickly can I launch?", a: "Most users can create and launch their first bot in under 10 minutes. The embed code takes 30 seconds to install, and the Flow Builder lets you design conversations in minutes." },
  { q: "What payment methods do you accept?", a: "We accept all major credit cards (Visa, Mastercard, American Express), debit cards, and PayPal. Enterprise plans can also be invoiced monthly or annually." },
  { q: "Can I cancel my subscription?", a: "Yes, you can cancel anytime from the Plans page. Your access continues until the end of the current billing period. No cancellation fees or contracts." },
  { q: "Is my data secure?", a: "Yes. All data is encrypted in transit (TLS 1.3) and at rest. We use industry-standard security practices, including regular security audits, access controls, and GDPR-compliant data handling." },
  { q: "Do you offer refunds?", a: "Yes, we offer a 14-day money-back guarantee on all paid plans. If you're not satisfied, contact our support team for a full refund." },
];

const HelpCenter = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [expandedCat, setExpandedCat] = useState(null);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [faqLeft, setFaqLeft] = useState(false);
  const [faqRight, setFaqRight] = useState(false);

  const filtered = useMemo(() => {
    if (!search.trim()) return { cats: categories, popular: popularArticles };
    const q = search.toLowerCase();
    const filteredCats = categories.map(cat => ({
      ...cat,
      articles: cat.articles.filter(a => a.title.toLowerCase().includes(q) || a.desc?.toLowerCase().includes(q)),
    })).filter(cat => cat.articles.length > 0 || cat.title.toLowerCase().includes(q) || cat.desc.toLowerCase().includes(q));
    const filteredPopular = popularArticles.filter(p => p.title.toLowerCase().includes(q));
    return { cats: filteredCats, popular: filteredPopular };
  }, [search]);

  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <Box sx={{ position: "relative", textAlign: "center", py: { xs: 10, md: 14 }, background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`, overflow: "hidden" }}>
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`, backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)", WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)", pointerEvents: "none" }} />
        <motion.div animate={{ y: [0, -40, 0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }} style={{ position: "absolute", top: "5%", left: "-5%", width: 500, height: 500, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`, filter: "blur(60px)", pointerEvents: "none" }} />
        <motion.div animate={{ y: [0, 40, 0] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }} style={{ position: "absolute", bottom: "-10%", right: "-10%", width: 600, height: 600, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`, filter: "blur(80px)", pointerEvents: "none" }} />
        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: "2rem", md: "3rem" }, background: "linear-gradient(135deg, #38bdf8, #a855f7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", mb: 1 }}>
              How Can We Help?
            </Typography>
            <Typography sx={{ color: "#94a3b8", fontSize: "1rem", mb: 3 }}>
              Search our knowledge base for answers, guides, and tutorials.
            </Typography>
            <TextField fullWidth placeholder="Search articles..." size="medium" value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#64748b" }} /></InputAdornment>,
                sx: { borderRadius: "12px", bgcolor: "rgba(255,255,255,0.08)", "& fieldset": { border: "none" }, "& input": { color: "#fff", fontSize: "0.95rem" } }
              }} />
          </motion.div>
        </Container>
      </Box>

      {!search && (
        <Container maxWidth="lg" sx={{ mt: -2, mb: 5, position: "relative", zIndex: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
            {popularArticles.map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Box onClick={() => { setExpandedCat(item.category); setExpandedArticle(null); setTimeout(() => document.getElementById("cat-" + item.category)?.scrollIntoView({ behavior: "smooth" }), 100); }}
                  sx={{ px: 2.5, py: 1.2, borderRadius: "20px", bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer", display: "flex", alignItems: "center", gap: 1, transition: "all 0.2s", "&:hover": { bgcolor: "rgba(56,189,248,0.1)", borderColor: "rgba(56,189,248,0.3)" } }}>
                  <item.icon size={16} color="#38bdf8" />
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#e2e8f0" }}>{item.title}</Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Container>
      )}

      {search && filtered.cats.length === 0 && (
        <Container maxWidth="lg" sx={{ textAlign: "center", py: 6 }}>
          <HelpCircle size={48} color="#64748b" style={{ marginBottom: 16 }} />
          <Typography sx={{ color: "#e2e8f0", fontWeight: 600, fontSize: "1.1rem", mb: 1 }}>No results found</Typography>
          <Typography sx={{ color: "#64748b", fontSize: "0.9rem", mb: 2 }}>Try different keywords or browse categories below.</Typography>
          <Button variant="outlined" onClick={() => setSearch("")} sx={{ borderRadius: "8px", textTransform: "none", borderColor: "rgba(255,255,255,0.2)", color: "#94a3b8" }}>Clear Search</Button>
        </Container>
      )}

      <Box sx={{ background: "linear-gradient(180deg, #0a0e27 0%, #050816 100%)" }}>
        <Container sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={3}>
            {categories.map((cat, ci) => {
              const matchingArticles = search ? filtered.cats.find(c => c.id === cat.id)?.articles || [] : cat.articles;
              if (search && matchingArticles.length === 0 && !cat.title.toLowerCase().includes(search.toLowerCase()) && !cat.desc.toLowerCase().includes(search.toLowerCase())) return null;
              return (
                <Grid item xs={12} sm={6} md={4} key={cat.id} id={"cat-" + cat.id} sx={{ display: "flex" }}>
                  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: ci * 0.05 }} style={{ width: "100%" }}>
                    <Box sx={{ p: 2.5, borderRadius: 4, bgcolor: "rgba(255,255,255,0.04)", backdropFilter: "blur(16px)", border: expandedCat === cat.id ? `1px solid ${cat.color}44` : "1px solid rgba(255,255,255,0.08)", transition: "all 0.3s", height: "100%" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, cursor: "pointer" }} onClick={() => setExpandedCat(expandedCat === cat.id ? null : cat.id)}>
                        <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: `${cat.color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <cat.icon size={20} color={cat.color} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>{cat.title}</Typography>
                            <Chip label={matchingArticles.length + " articles"} size="small" sx={{ bgcolor: `${cat.color}12`, color: cat.color, fontSize: "0.6rem", height: 20, fontWeight: 600 }} />
                          </Box>
                          <Typography sx={{ fontSize: "0.8rem", color: "#64748b", mt: 0.2 }}>{cat.desc}</Typography>
                        </Box>
                        <ChevronRight size={18} color="#64748b" style={{ transform: expandedCat === cat.id ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} />
                      </Box>
                      {expandedCat === cat.id && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} transition={{ duration: 0.3 }}>
                          <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.06)", pt: 1.5 }}>
                            {(search ? matchingArticles : cat.articles).map((article, ai) => (
                              <Box key={ai}>
                                <Box onClick={() => setExpandedArticle(expandedArticle === `${cat.id}-${ai}` ? null : `${cat.id}-${ai}`)}
                                  sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.2, borderRadius: 2, cursor: "pointer", transition: "all 0.15s", "&:hover": { bgcolor: "rgba(255,255,255,0.04)" } }}>
                                  <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: cat.color, flexShrink: 0 }} />
                                  <Typography sx={{ flex: 1, fontSize: "0.85rem", color: expandedArticle === `${cat.id}-${ai}` ? "#38bdf8" : "#cbd5e1", fontWeight: expandedArticle === `${cat.id}-${ai}` ? 600 : 400, transition: "all 0.15s" }}>
                                    {article.title}
                                  </Typography>
                                  <ChevronDown size={14} color="#64748b" style={{ transform: expandedArticle === `${cat.id}-${ai}` ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                                </Box>
                                {expandedArticle === `${cat.id}-${ai}` && (
                                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} transition={{ duration: 0.2 }}>
                                    <Box sx={{ px: 1.5, pb: 1.5, borderLeft: `2px solid ${cat.color}33`, ml: 1.5, mt: 0.5 }}>
                                      {article.content}
                                    </Box>
                                  </motion.div>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </motion.div>
                      )}
                    </Box>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{ mt: 8 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.3rem", textAlign: "center", color: "#fff", mb: 4 }}>
              <HelpCircle size={22} style={{ marginRight: 8, verticalAlign: "middle", color: "#38bdf8" }} />
              Frequently Asked Questions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {faqLeftItems.map((item, i) => (
                  <Accordion key={i} expanded={faqLeft === i} onChange={() => setFaqLeft(faqLeft === i ? false : i)}
                    sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px !important", mb: 1.5, border: "1px solid rgba(255,255,255,0.06)", "&:before": { display: "none" }, "&.Mui-expanded": { borderColor: "rgba(56,189,248,0.2)" } }}>
                    <AccordionSummary expandIcon={<ChevronDown size={18} color="#94a3b8" />} sx={{ px: 2.5, py: 0.5, "& .MuiAccordionSummary-content": { my: 1.5 } }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#e2e8f0" }}>{item.q}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                      <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7 }}>{item.a}</Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Grid>
              <Grid item xs={12} md={6}>
                {faqRightItems.map((item, i) => (
                  <Accordion key={i} expanded={faqRight === i} onChange={() => setFaqRight(faqRight === i ? false : i)}
                    sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px !important", mb: 1.5, border: "1px solid rgba(255,255,255,0.06)", "&:before": { display: "none" }, "&.Mui-expanded": { borderColor: "rgba(56,189,248,0.2)" } }}>
                    <AccordionSummary expandIcon={<ChevronDown size={18} color="#94a3b8" />} sx={{ px: 2.5, py: 0.5, "& .MuiAccordionSummary-content": { my: 1.5 } }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#e2e8f0" }}>{item.q}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                      <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.7 }}>{item.a}</Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mt: 6, textAlign: "center", p: 4, borderRadius: 4, bgcolor: "rgba(255,255,255,0.05)", backdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.08)", transition: "all 0.3s", "&:hover": { transform: "translateY(-4px)", borderColor: "rgba(56,189,248,0.3)", boxShadow: "0 12px 40px rgba(56,189,248,0.1)" } }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#fff", mb: 1 }}>Still need help?</Typography>
            <Typography sx={{ color: "#94a3b8", mb: 3, maxWidth: 400, mx: "auto" }}>Our support team and community are here for you.</Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              <Button variant="contained" onClick={() => navigate("/contact")} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, background: "linear-gradient(135deg, #4F46E5, #7C3AED)", boxShadow: "0 4px 15px rgba(79,70,229,0.4)", "&:hover": { boxShadow: "0 6px 25px rgba(79,70,229,0.6)" } }}>
                <Mail size={16} style={{ marginRight: 8 }} /> Contact Support
              </Button>
              <Button variant="outlined" onClick={() => navigate("/docs")} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, borderColor: "rgba(255,255,255,0.2)", color: "#cbd5e1", "&:hover": { borderColor: "#fff", color: "#fff", bgcolor: "rgba(255,255,255,0.05)" } }}>
                <BookOpen size={16} style={{ marginRight: 8 }} /> View Full Documentation
              </Button>
              <Button variant="outlined" onClick={() => navigate("/community")} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, borderColor: "rgba(255,255,255,0.2)", color: "#cbd5e1", "&:hover": { borderColor: "#fff", color: "#fff", bgcolor: "rgba(255,255,255,0.05)" } }}>
                <Users size={16} style={{ marginRight: 8 }} /> Join Community
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HelpCenter;
